<template>
  <div class="help-sidebar__root" :style="rootStyle" @mouseleave="onSidebarMouseleave" @mouseover="onSidebarMouseover">
    <back-to-top></back-to-top>
    <div :class="['help-sidebar__content', isSidebarClose && 'close', isShowAddToDesktop && 'has-desktop']">
      <template v-if="isSidebarClose">
        <div
          :class="['help-more item', isItemActive(HELP_ITEM.more) && 'active']"
          @mouseover="onHoverItem(HELP_ITEM.more)"
          @click="onCloseClick"
        >
          <IconMyIconHelp class="icon-help"></IconMyIconHelp>
          <span class="label">更多</span>
        </div>
      </template>
      <template v-else>
        <el-popover
          :visible="_isConsultPopShow"
          placement="left-start"
          effect="light"
          :disabled="isSidebarClose || !isItemActive(HELP_ITEM.consult)"
          :showAfter="250"
          :closeDelay="200"
          :width="136"
          popperClass="help-dr-code-pop"
          @after-enter="onConsultPopShow"
          @after-leave="onConsultPopHide"
        >
          <template #reference>
            <div
              :class="[isItemActive(HELP_ITEM.consult) && 'active', 'consult item']"
              @mouseover="onHoverItem(HELP_ITEM.consult)"
            >
              <IconMyIconHelpSidebarHelp class="consult-icon"></IconMyIconHelpSidebarHelp>
              <span class="label">咨询</span>
            </div>
          </template>
          <template #default><help-qrcode v-click-outside="onClickOutsideQrCode"></help-qrcode></template>
        </el-popover>
        <!-- 添加至桌面 -->
        <div
          v-if="isShowAddToDesktop"
          :class="[isItemActive(HELP_ITEM.desktop) && 'active', 'help item desktop']"
          @mouseover="onHoverItem(HELP_ITEM.desktop)"
          @click="onClickItem(HELP_ITEM.desktop)"
        >
          <IconMyIconHelpAddToDesktop class="help-icon"></IconMyIconHelpAddToDesktop>
          <span class="label">添加<br />桌面</span>
        </div>
        <el-popover
          ref="helpPopRef"
          :visible="_isHelpPopShow"
          placement="left"
          effect="light"
          :disabled="isSidebarClose || !isItemActive(HELP_ITEM.help)"
          :showAfter="250"
          :closeDelay="200"
          :width="276"
          popperClass="help-pop"
          @after-enter="onHelpPopShow"
          @after-leave="onHelpPopHide"
        >
          <template #reference>
            <div
              :class="[isItemActive(HELP_ITEM.help) && 'active', 'help item']"
              @mouseover="onHoverItem(HELP_ITEM.help)"
            >
              <IconMyIconHelpSidebar class="help-icon"></IconMyIconHelpSidebar>
              <span class="label">帮助</span>
            </div>
          </template>
          <template #default>
            <help-step-content
              v-click-outside="onClickOutside"
              @on-create-app-click="onCreateAppClick"
              @on-config-form-click="onConfigFormClick"
              @on-define-service-click="onDefineServiceClick"
              @on-report-click="onReportClick"
            ></help-step-content>
          </template>
        </el-popover>
        <div
          :class="[isItemActive(HELP_ITEM.feedback) && 'active', 'feedback item']"
          @mouseover="onHoverItem(HELP_ITEM.feedback)"
          @click="onFeedbackClick"
        >
          <IconMyIconHelpFeedback class="feedback-icon"></IconMyIconHelpFeedback>
          <span class="label">反馈</span>
        </div>
        <div
          :class="[isItemActive(HELP_ITEM.close) && 'active', 'close item']"
          @click="onCloseClick"
          @mouseover="onHoverItem(HELP_ITEM.close)"
        >
          <IconMyIconHelpSidebarClose class="close-icon"></IconMyIconHelpSidebarClose>
          <span class="label">收起</span>
        </div>
      </template>
    </div>
  </div>

  <help-step-dialog
    v-if="showHelpStep"
    :helpStepData="helpStepData"
    @on-help-step-dialog-closed="onHelpStepDialogClosed"
  ></help-step-dialog>
</template>

<script setup lang="ts">
import BackToTop from '@/features/back-to-top/back-to-top.vue';
import { checkWpsEnvIsNewPage } from '@/utils/env';
import { openFeedbackPage } from '@/utils/feedback-wps';
import { addToDesktop, isSupportWpsFnAsync } from '@/utils/wps-fn';
import { ClickOutside as vClickOutside, useZIndex } from 'element-plus';
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';
import { getGuideEmitter } from '../guide/guide.emitter';
import { IHelpStep } from './help-icon';
import {
  dwOnHelpClick,
  dwOnHelpCloseClick,
  dwOnHelpIconHover,
  dwOnHelpMoreHover,
  dwOnHelpPopoverShow,
  dwOnHelpQrCodeShow,
  dwOnHelpUserFeedbackClick,
} from './help-icon.dw';

const helpStepDialog = defineAsyncComponent(() => import('./help-step-dialog.vue'));

const HelpQrcode = defineAsyncComponent(() => import('@/features/help-icon/help-qrcode.vue'));

const HelpStepContent = defineAsyncComponent(() => import('@/features/help-icon/help-step-content.vue'));

// #region 新逻辑

/** 帮助项 */
const HELP_ITEM = {
  consult: 'consult',
  desktop: 'desktop',
  help: 'help',
  feedback: 'feekback',
  close: 'close',
  more: 'more',
};

/** 侧边栏是否展开 */
const isSidebarClose = ref(false);
/** 侧边栏展开动画是否完成 */
const isSidebarExpandAnimationDone = ref(true);
/** 侧边栏展开动画耗时 */
const expandCloseAnimationTime = ref(200);
/** 侧边栏展开动画耗时 */
const expandCloseAnimationTimeCss = computed(() => {
  return `${expandCloseAnimationTime.value}ms`;
});
/** 当前激活的项 */
const activeItem = ref('');
// const helpPopoverShow = ref(false);

const isConsultPopShow = ref(false);
const isHelpPopShow = ref(false);
const _isHelpPopShow = ref<null | boolean>(null);
const _isConsultPopShow = ref<null | boolean>(null);
const helpPopRef = ref();

/** 当前项鼠标hover */
const onHoverItem = (code: string) => {
  if (!isSidebarExpandAnimationDone.value) {
    // 动画未完成 不执行逻辑
    return;
  }

  activeItem.value = code;
  switch (code) {
    case HELP_ITEM.help:
      dwOnHelpIconHover();
      break;
    default:
      break;
  }
};

/** 判断是否显示添加至桌面这个图标 */
const isShowAddToDesktop = ref(false);
(async () => {
  if (checkWpsEnvIsNewPage()) {
    // 新建页内需要判断添加桌面快捷方式这个接口是否支持
    isShowAddToDesktop.value = await isSupportWpsFnAsync({ api: 'createShortcut' });
  }
})();

/** 当前项鼠标click */
const onClickItem = (code: string) => {
  switch (code) {
    case HELP_ITEM.desktop:
      addToDesktop();
      break;
    default:
      break;
  }
};

/** 判断当前项是否激活状态 */
const isItemActive = (code: string) => {
  return activeItem.value === code;
};

/** 侧边栏移出 */
const isMouseoverSidebar = ref(false);
/** 鼠标移入帮助侧边栏事件 */
const onSidebarMouseover = () => {
  isMouseoverSidebar.value = true;
};
/** 鼠标移出帮助侧边栏事件 */
const onSidebarMouseleave = () => {
  isMouseoverSidebar.value = false;
  updateActiveItem();
};
/** 更新 ActiveItem */
const updateActiveItem = () => {
  // 鼠标不在内部，并且popover都没显示时重置 activeItem
  if ((!isMouseoverSidebar.value && !isConsultPopShow.value && !isHelpPopShow.value) || isSidebarClose.value) {
    activeItem.value = '';
  }
};
/** 折叠展开定时器 */
let expandCloseAnimationTimer = NaN;
/** 收起按钮点击事件 */
const onCloseClick = () => {
  if (!isSidebarClose.value) {
    dwOnHelpCloseClick();
  } else {
    dwOnHelpMoreHover();
  }

  isSidebarClose.value = !isSidebarClose.value;

  // 标记动画是否完成
  expandCloseAnimationTimer && window.clearTimeout(expandCloseAnimationTimer);
  isSidebarExpandAnimationDone.value = false;
  expandCloseAnimationTimer = window.setTimeout(() => {
    isSidebarExpandAnimationDone.value = true;
  }, expandCloseAnimationTime.value);
};

/** 咨询二维码显示 */
const onConsultPopShow = () => {
  activeItem.value = HELP_ITEM.consult;
  isConsultPopShow.value = true;

  dwOnHelpQrCodeShow();
};
/** 咨询二维码隐藏 */
const onConsultPopHide = () => {
  isConsultPopShow.value = false;
  if (_isConsultPopShow.value) {
    // popover 改为自动模式，非boolean值就行
    _isConsultPopShow.value = null;
  }
  updateActiveItem();
};

/** 帮助step显示 */
const onHelpPopShow = () => {
  activeItem.value = HELP_ITEM.help;
  isHelpPopShow.value = true;

  dwOnHelpPopoverShow();
};
/** 咨询二维码隐藏 */
const onHelpPopHide = () => {
  isHelpPopShow.value = false;
  if (_isHelpPopShow.value) {
    // popover 改为自动模式，非boolean值就行
    _isHelpPopShow.value = null;
  }
  updateActiveItem();
};

const onCreateAppClick = (name: string) => {
  onHelpStepItemClick('createApp');
  dwOnHelpClick(name);
};

const onConfigFormClick = (name: string) => {
  onHelpStepItemClick('configForm');
  dwOnHelpClick(name);
};

const onDefineServiceClick = (name: string) => {
  onHelpStepItemClick('configProcess');
  dwOnHelpClick(name);
};

const onReportClick = (name: string) => {
  onHelpStepItemClick('createReport');
  dwOnHelpClick(name);
};

/** 显示指引 */
const onHelpStepShow = () => {
  getGuideEmitter().on('show-help', () => {
    onHelpPopShow();
    _isHelpPopShow.value = true;
  });
};

/** 显示帮助二维码 */
const onHelpQrcodeShow = () => {
  getGuideEmitter().on('show-help-qrcode', () => {
    onConsultPopShow();
    _isConsultPopShow.value = true;
  });
};

const onClickOutside = () => {
  onHelpPopHide();
};

const onClickOutsideQrCode = () => {
  onConsultPopHide();
};

onMounted(() => {
  onHelpStepShow();
  onHelpQrcodeShow();
});
// #endregion

const nextZIndex = useZIndex().nextZIndex();
const rootStyle = computed(() => {
  return { zIndex: nextZIndex };
});

const onFeedbackClick = () => {
  openFeedbackPage();

  dwOnHelpUserFeedbackClick();
};

const createAppTitle = ref('如何创建应用？');
const configFormTitle = ref('如何配置表单？');
const configProcessTitle = ref('如何配置流程？');
const createReportTitle = ref('如何创建报表？');
// const OperationManualTitle = ref('操作手册');
const helpData = {
  createApp: [
    {
      title: createAppTitle.value,
      info: 'WPS提供了丰富的应用模板，可以满足多种场景需要。选择您需要的模板，进入详情页查看具体介绍。',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/2T-qinliu/mo_ban.png',
    },
    {
      title: createAppTitle.value,
      info: '点击【从模板创建应用】，即可一键创建自己的专属应用。',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/2T-qinliu/mo_ban_xiang_qing.png',
    },
    {
      title: createAppTitle.value,
      info: 'WPS轻应用由表单、流程和报表组成，具有数据记录、协作、审批、可视化查看的功能。',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/2T-qinliu/ying_yong.webp',
    },
  ],
  configForm: [
    {
      title: configFormTitle.value,
      info: '表单由若干字段组成，用来填报和记录数据。',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/ql2.webp',
    },
    {
      title: configFormTitle.value,
      info: '编辑应用时，进入【表单设计】页面，在左侧字段列表中由丰富的表单字段供您选择，点击即可添加。',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/biaodan1.mp4',
    },
    {
      title: configFormTitle.value,
      info: '【表单设计】页面的右侧是字段编辑面板，在这里可以灵活配置字段的标题、默认内容等树形。',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/biaodan2.mp4',
    },
    {
      title: configFormTitle.value,
      info: '【表单设计】页面的中间区域为字段展示面板，在这里可以调整表单名称、字段位置等，配置结束后，记得保存和发布哦。',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/biaodan3-1.mp4',
    },
  ],
  configProcess: [
    {
      title: configProcessTitle.value,
      info: '配置好表单后，可以为其添加工作流程，让表单按照一定的业务流程规则进行流转。',
      info2: '如，财务审批、请假审批、更新资产状态等',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/ql3.webp',
    },
    {
      title: configProcessTitle.value,
      info: '在【添加流程】页面，点击加号，开始为表单添加流程节点。',
      info2:
        '节点类型包括：审批节点、填写节点、抄送节点，以及能够让流程自动运转的Q-Robot节点，Q-Robot可以自动添加、更新数据，还能自动发邮件和短信哦！',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/liucheng1.mp4',
    },
    {
      title: configProcessTitle.value,
      info: '点击节点，即可对流程节点进行详细设置，比如字段权限、超时提醒、提交校验等。',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/liucheng2.mp4',
    },
    {
      title: configProcessTitle.value,
      info: '除此之外，还可以添加子分支，再复杂的流程也可以轻松搞定。',
      info2: '一切就绪，发布应用！每一次应用更改后，都需要重新发布才能覆盖原有应用设置哦！',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/liucheng3.mp4',
    },
  ],
  createReport: [
    {
      title: createReportTitle.value,
      info: '报表可以将通过表单收集到的数据，用多种图表进行可视化展示、计算和分析。',
      info2: '比如，订单趋势图、销售总额、订单汇总等。',
      img: 'https://vasvip-pub.wpscdn.cn/prod/2T/ql1.webp',
    },
    {
      title: createReportTitle.value,
      info: '进入一个应用，在【统计】的标签下，点击“创建报表”，即可体验强大的自定义报表。在【报表编辑】页面，左侧是报表类型，点击即可选择。',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/baobiao1.mp4',
    },
    {
      title: createReportTitle.value,
      info: '右侧是报表编辑面板，在这里可以配置字段的标题、操作权限、数据范围等属性。',
      info2: '配置好报表后，点击保存即可！报表即刻生成，实时刷新！',
      video: 'https://vasvip-pub.wpscdn.cn/prod/Qingliu/video/baobiao2-1.mp4',
    },
  ],
} as Record<string, IHelpStep[]>;

const showHelpStep = ref(false);
const helpStepData = ref<IHelpStep[]>();

const onHelpStepItemClick = (type = '') => {
  if (['createApp', 'configForm', 'configProcess', 'createReport'].indexOf(type) !== -1) {
    const data = helpData[type];
    helpStepData.value = data;
    showHelpStep.value = true;

    dwOnHelpClick(data[0]?.title);
  }
};

const onHelpStepDialogClosed = () => {
  showHelpStep.value = false;
};
</script>

<style lang="scss" scoped>
.help-sidebar {
  &__root {
    cursor: pointer;
    position: fixed;
    bottom: 16px;
    right: 16px;
    width: 40px;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.1);
    background: var(--lowcode-background-01);
    border-radius: 24px;
    border: 1px solid var(--lowcode-custom-border-14);
    font-size: 0;

    // 正常模式下
    .item {
      width: 40px;
      height: 40px;
      border-radius: 20px;
      text-align: center;
      line-height: 40px;

      > svg {
        // margin-top: 8px;
        height: 40px;
      }

      .label {
        text-align: center;
        display: none;
        width: 40px;
        font-size: 12px;
        color: #ffffff;
        line-height: 40px;
        user-select: none;
      }
    }

    // 鼠标hover后
    .active {
      background: #417ff9;

      &:active {
        background: #1f72f1;
      }

      > svg {
        display: none;
      }
      .label {
        display: inline-block;
      }

      &.desktop {
        display: flex;
        align-items: center;
        justify-content: center;

        .label {
          line-height: 12px;
        }
      }
    }
  }

  &__content {
    transition: height v-bind(expandCloseAnimationTimeCss) ease-in-out;
    height: 160px;
    overflow: hidden;
    &.has-desktop {
      height: 200px;
    }

    .help-more {
      display: none;
    }

    &.close {
      height: 40px;
      .help-more {
        display: block;

        &:hover {
          > svg {
            display: none;
          }

          > .label {
            display: block;
          }
        }
      }
    }
  }
}

.help-icon {
  &-root {
    box-sizing: border-box;
    position: fixed;
    bottom: 16px;
    right: 16px;
    width: 48px;
    height: 48px;
    background: var(--lowcode-background-01);
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--lowcode-custom-border-14);
    border-radius: 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .icon-help {
      display: inline-block;
      width: 24px;
      height: 24px;
    }

    .help-label {
      display: none;
      width: 24px;
      height: 16px;
      font-size: 12px;
      color: #ffffff;
      line-height: 16px;
    }

    &:hover {
      background: #2c71fa;
      border-color: #2c71fa;

      .icon-help:deep(path) {
        fill: #fff;
      }

      .help-label {
        display: block;
        transform: translateY(-2px);
      }
    }

    &:active {
      background: #1c53d4;
      border-color: #1c53d4;
      .icon-help:deep(path) {
        fill: #fff;
      }
    }
  }
}

.img-web {
  &__online {
    box-sizing: border-box;
    // width: 136px;
    height: 158px;
    background: var(--lowcode-background-03);
    padding: 20px 20px 10px 20px;
  }

  &__img {
    display: inline-block !important;
    width: 96px !important;
    height: 96px !important;
  }

  &__label {
    text-align: center;
    margin-top: 10px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }
}
</style>

<style lang="scss">
.el-popover.el-popper.popover-help-level-1st {
  box-sizing: border-box;
  width: 150px;
  height: 92px;
  background: var(--lowcode-background-03);
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  border: 1px solid var(--lowcode-custom-border-09);
  padding: 4px 2px;
  .help {
    &-list {
      height: 100%;
      width: 100%;
      color: var(--lowcode-text-02);
    }

    &-item {
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 28px;
      border-radius: 2px;

      &:hover {
        background: var(--lowcode-status-02);
      }
    }

    &-icon-start {
      width: 16px;
      height: 16px;
      margin-left: 6px;
      margin-right: 8px;
      flex: none;
      path {
        fill: var(--lowcode-icon-04);
      }
    }

    &-icon-end {
      width: 16px;
      margin-right: 8px;
      flex: none;
      path {
        fill: var(--lowcode-icon-04);
      }
    }

    &-label {
      flex: 1;
    }
  }
}

.el-popover.el-popper.popover-help-online {
  box-sizing: border-box;
  background: var(--lowcode-background-03);
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  border-radius: 2px;
  border: 1px solid var(--lowcode-custom-border-09);
  padding: 0;
  font-size: 0;
  min-width: 136px;
  .img-help-online,
  img {
    display: inline-block;
    width: 100%;
    height: 100%;
    font-size: 0;
  }
}

.el-popover.el-popper.popover-help-guide {
  background: var(--lowcode-background-03);
  min-width: 114px;
  width: 114px;
  padding: 4px 0;
  box-sizing: border-box;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--lowcode-custom-border-09);
  border-radius: 2px;
  .guide {
    &-item {
      cursor: pointer;
      box-sizing: border-box;
      padding-left: 16px;
      height: 28px;
      line-height: 28px;
      color: var(--lowcode-text-02);
      &:hover {
        background: var(--lowcode-status-02);
      }
    }
  }
}

.el-popover.el-popper.help-dr-code-pop {
  background: var(--lowcode-dlg-background-01);
  padding: 16px 16px 12px 16px;
  min-width: 136px;
  width: 136px;
  box-sizing: border-box;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(38, 46, 62, 0.15);

  .el-popper__arrow::before {
    background: var(--lowcode-dlg-background-01);
    border-color: transparent;
  }
}

.el-popover.el-popper.help-pop {
  padding: 0;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.3);
  background: var(--lowcode-dlg-background-01);
  border-color: var(--lowcode-custom-border-14);
  .el-popper__arrow::before {
    background: var(--lowcode-dlg-background-01);
    border-color: transparent;
  }
}
</style>
